import axios, { AxiosInstance } from 'axios';
import type {
  BillingCycle,
  Contract,
  CustomerUser,
  MfaSetup,
  MfaStatus,
  Order,
  Product,
} from './types';

const TOKEN_KEY = 'juni_cc_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

const client: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !window.location.pathname.startsWith('/login')) {
      clearToken();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  },
);

export interface LoginResult {
  token?: string;
  user?: CustomerUser;
  /** Password accepted, but a second factor is still outstanding. */
  mfaRequired?: boolean;
  challenge?: string;
}

export const auth = {
  register: async (payload: {
    companyName: string;
    billingEmail: string;
    name: string;
    email: string;
    password: string;
  }): Promise<{ token: string; user: CustomerUser }> => {
    const { data } = await client.post('/customer-auth/register', payload);
    return data;
  },
  login: async (email: string, password: string): Promise<LoginResult> => {
    const { data } = await client.post('/customer-auth/login', { email, password });
    return data;
  },
  verifyMfa: async (
    challenge: string,
    code: string,
  ): Promise<{ token: string; user: CustomerUser }> => {
    const { data } = await client.post('/customer-auth/2fa/verify', { challenge, code });
    return data;
  },
  mfaStatus: async (): Promise<MfaStatus> => {
    const { data } = await client.get('/customer-auth/2fa/status');
    return data;
  },
  mfaSetup: async (): Promise<MfaSetup> => {
    const { data } = await client.post('/customer-auth/2fa/setup');
    return data;
  },
  mfaEnable: async (code: string): Promise<{ backupCodes: string[] }> => {
    const { data } = await client.post('/customer-auth/2fa/enable', { code });
    return data;
  },
  mfaDisable: async (password: string, code: string): Promise<{ message: string }> => {
    const { data } = await client.post('/customer-auth/2fa/disable', { password, code });
    return data;
  },
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const { data } = await client.post('/customer-auth/forgot-password', { email });
    return data;
  },
  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const { data } = await client.post('/customer-auth/reset-password', { token, password });
    return data;
  },
  me: async (): Promise<CustomerUser> => {
    const { data } = await client.get('/customer-auth/me');
    return data;
  },
};

export const portal = {
  products: async (): Promise<Product[]> => {
    const { data } = await client.get('/portal/products');
    return data;
  },
  contracts: async (): Promise<Contract[]> => {
    const { data } = await client.get('/portal/contracts');
    return data;
  },
  orders: async (): Promise<Order[]> => {
    const { data } = await client.get('/portal/orders');
    return data;
  },
  checkout: async (payload: {
    productId: string;
    planId: string;
    billingCycle: BillingCycle;
    configuration?: Record<string, unknown>;
  }): Promise<{ orderId: string; checkoutUrl: string }> => {
    const { data } = await client.post('/portal/checkout', payload);
    return data;
  },
  cancelContract: async (id: string): Promise<{ order: Order; instance: Contract }> => {
    const { data } = await client.post(`/portal/contracts/${id}/cancel`);
    return data;
  },
  changePlan: async (
    id: string,
    payload: { planId: string; billingCycle: BillingCycle },
  ): Promise<{ orderId: string; orderType: string; checkoutUrl: string }> => {
    const { data } = await client.post(`/portal/contracts/${id}/change-plan`, payload);
    return data;
  },
};

export default client;
